import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import {
  Loader2, Wine, Sparkles, CheckCircle2, X, Plus, Check, ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useImageGeneration } from "../../context/ImageGenerationContext";
import { QuickAddIngredientDialog } from "../inventory/QuickAddIngredientDialog";

/**
 * "Nueva bebida" in the Recipes/Menu area.
 *
 * Simplified: this dialog ONLY creates the menu dish and links it to an
 * existing inventory ingredient. Barcode scanning / Open Food Facts /
 * beverage shelf photo now live in InventoryPage → "Alta rápida".
 */
export function NewBeverageDialog({ open, onOpenChange, restaurantId, onBeverageCreated }) {
  const { handleGenerateDishImages } = useImageGeneration();

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    menuId: "",
    categoryId: "",
    ingredientId: "",
  });
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [aiPreviewLoading, setAiPreviewLoading] = useState(false);
  const [pendingDishId, setPendingDishId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const aiAbortRef = useRef(null);

  // ── Load menus + ingredients when the dialog opens ────────────────
  useEffect(() => {
    if (!open || !restaurantId) return;
    (async () => {
      try {
        const [m, ings] = await Promise.all([
          api.getMenus(restaurantId),
          api.getIngredients(restaurantId),
        ]);
        setMenus(m);
        setIngredients(ings);
        if (m.length === 1) setForm((f) => ({ ...f, menuId: m[0].menu_id }));
      } catch {
        toast.error("Error al cargar datos");
      }
    })();
  }, [open, restaurantId]);

  // Load categories when menu changes, auto-pick "Bebidas"
  useEffect(() => {
    if (!form.menuId) { setCategories([]); return; }
    (async () => {
      try {
        const cats = await api.getCategories(form.menuId);
        setCategories(cats);
        const bev = cats.find((c) => (c.name || "").toLowerCase() === "bebidas")
                 || cats.find((c) => (c.name || "").toLowerCase().includes("bebida"));
        if (bev) setForm((f) => ({ ...f, categoryId: bev.category_id }));
      } catch { /* ignore */ }
    })();
  }, [form.menuId]);

  const resetAll = useCallback(() => {
    setForm({ name: "", description: "", price: "", image_url: "", menuId: "", categoryId: "", ingredientId: "" });
    setPendingDishId(null);
    setAiPreviewLoading(false);
    try { aiAbortRef.current?.abort(); } catch { /* noop */ }
    aiAbortRef.current = null;
  }, []);

  useEffect(() => { if (!open) resetAll(); }, [open, resetAll]);

  const reloadIngredients = useCallback(async () => {
    try {
      const ings = await api.getIngredients(restaurantId);
      setIngredients(ings);
    } catch { /* ignore */ }
  }, [restaurantId]);

  const selectedIngredient = useMemo(
    () => ingredients.find((i) => i.ingredient_id === form.ingredientId),
    [ingredients, form.ingredientId],
  );

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!form.menuId) { toast.error("Selecciona un menú"); return; }
    setCreating(true);
    try {
      let dish;
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        price: form.price ? parseFloat(form.price) : 0,
        category_id: form.categoryId || undefined,
        is_active: true,
        allergens: [],
        tags: ["bebida"],
        image_url: form.image_url || undefined,
      };
      if (pendingDishId) {
        dish = await api.updateDish(pendingDishId, payload);
      } else {
        dish = await api.createDish(form.menuId, payload);
      }

      // Link to inventory ingredient via recipe (so TPV can discount stock)
      if (form.ingredientId && dish?.dish_id) {
        try {
          await api.createRecipe(dish.dish_id, {
            dish_id: dish.dish_id,
            ingredients: [{ ingredient_id: form.ingredientId, quantity: 1 }],
            preparation_steps: [],
            preparation_time: 0,
            servings: 1,
          });
        } catch (e) {
          console.warn("No se pudo vincular el ingrediente", e);
          toast.warning("Bebida creada pero no se pudo vincular al inventario");
        }
      }

      toast.success(`"${dish.name || form.name}" añadida a Bebidas`);
      onBeverageCreated?.(dish);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.message || "Error al crear la bebida");
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!form.name.trim()) { toast.error("Introduce el nombre antes de generar la imagen"); return; }
    if (!form.menuId) { toast.error("Selecciona un menú antes"); return; }
    setAiPreviewLoading(true);
    try {
      let dishId = pendingDishId;
      if (!dishId) {
        const dish = await api.createDish(form.menuId, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          price: form.price ? parseFloat(form.price) : 0,
          category_id: form.categoryId || undefined,
          is_active: true,
          allergens: [],
          tags: ["bebida"],
        });
        dishId = dish.dish_id;
        setPendingDishId(dishId);
      }
      try { aiAbortRef.current?.abort(); } catch { /* noop */ }
      const ctrl = new AbortController();
      aiAbortRef.current = ctrl;
      const result = await api.generateDishImageSync(dishId, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      const newUrl = result?.image_url;
      if (newUrl) {
        const busted = `${newUrl}${newUrl.includes("?") ? "&" : "?"}_t=${Date.now()}`;
        setForm((f) => ({ ...f, image_url: busted }));
        toast.success('Imagen generada. Pulsa "Crear bebida" para guardar.');
      } else {
        toast.error("La IA no devolvió imagen");
      }
    } catch (err) {
      if (err?.name === "AbortError") toast.info("Generación cancelada");
      else toast.error(err?.message || "Error al generar imagen");
    } finally {
      aiAbortRef.current = null;
      setAiPreviewLoading(false);
    }
  };

  const handleCancelAiPreview = () => {
    try { aiAbortRef.current?.abort(); } catch { /* noop */ }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && pendingDishId) onBeverageCreated?.({ dish_id: pendingDishId });
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-beverage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wine className="w-5 h-5 text-primary" />
            Nueva bebida (Menú)
          </DialogTitle>
          <DialogDescription>
            Añade una bebida a la carta. Vincula un producto del inventario para que se descuente del stock al venderse.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <div className="flex gap-3 items-start">
            <div className="relative">
              {form.image_url ? (
                <img
                  src={form.image_url} alt={form.name}
                  className="w-28 h-28 rounded-lg object-contain bg-card border"
                  onError={(e) => { e.target.style.display = "none"; }}
                  data-testid="bev-preview-image"
                />
              ) : (
                <div className="w-28 h-28 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Wine className="w-7 h-7 text-muted-foreground" />
                </div>
              )}
              {aiPreviewLoading && (
                <div className="absolute inset-0 rounded-lg bg-card/80 dark:bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-1 z-10" data-testid="bev-ai-preview-overlay">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-[10px] text-center font-medium px-1">Generando...</p>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div>
                <Label htmlFor="bev-name">Nombre *</Label>
                <Input id="bev-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej. Coca-Cola 33cl" data-testid="bev-name-input" />
              </div>
              <div>
                <Label htmlFor="bev-price">Precio de venta (€)</Label>
                <Input id="bev-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="0.00" data-testid="bev-price-input" />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="bev-desc">Descripción</Label>
            <Textarea id="bev-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descripción o notas (opcional)" rows={2} data-testid="bev-description-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Menú *</Label>
              <Select value={form.menuId} onValueChange={(v) => setForm((f) => ({ ...f, menuId: v, categoryId: "" }))}>
                <SelectTrigger data-testid="bev-menu-select"><SelectValue placeholder="Selecciona menú" /></SelectTrigger>
                <SelectContent>{menus.map((m) => <SelectItem key={m.menu_id} value={m.menu_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.categoryId} onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v }))} disabled={!form.menuId}>
                <SelectTrigger data-testid="bev-category-select"><SelectValue placeholder={categories.length ? "Selecciona" : "—"} /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Ingredient picker */}
          <div>
            <Label>Producto del inventario (para descuento de stock)</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline" role="combobox"
                  className="w-full justify-between font-normal"
                  data-testid="bev-ingredient-picker"
                >
                  <span className="truncate">
                    {selectedIngredient
                      ? `${selectedIngredient.name} (${selectedIngredient.unit})`
                      : "Sin vincular — elige o crea"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar producto…" data-testid="bev-ingredient-search" />
                  <CommandList>
                    <CommandEmpty>Sin resultados</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__none__"
                        onSelect={() => { setForm((f) => ({ ...f, ingredientId: "" })); setPickerOpen(false); }}
                      >
                        <Check className={`mr-2 h-4 w-4 ${!form.ingredientId ? "opacity-100" : "opacity-0"}`} />
                        Sin vincular
                      </CommandItem>
                      {ingredients.map((ing) => (
                        <CommandItem
                          key={ing.ingredient_id}
                          value={`${ing.name} ${ing.unit}`}
                          onSelect={() => {
                            setForm((f) => ({ ...f, ingredientId: ing.ingredient_id }));
                            setPickerOpen(false);
                          }}
                          data-testid={`bev-ing-opt-${ing.ingredient_id}`}
                        >
                          <Check className={`mr-2 h-4 w-4 ${form.ingredientId === ing.ingredient_id ? "opacity-100" : "opacity-0"}`} />
                          <span className="flex-1 truncate">{ing.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{ing.unit}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup>
                      <CommandItem
                        value="__add__"
                        onSelect={() => { setPickerOpen(false); setQuickAddOpen(true); }}
                        data-testid="bev-ing-quick-add"
                      >
                        <Plus className="mr-2 h-4 w-4 text-primary" />
                        <span className="text-primary font-medium">Dar de alta en inventario…</span>
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-[11px] text-muted-foreground mt-1">
              Al servir esta bebida, el TPV descontará automáticamente el stock del producto vinculado.
            </p>
          </div>

          {form.image_url && !aiPreviewLoading && (
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {pendingDishId
                ? 'Imagen generada con IA. Pulsa "Crear bebida" para guardar, o "Regenerar" si prefieres otra.'
                : "Imagen lista."}
            </p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          {aiPreviewLoading ? (
            <Button variant="outline" onClick={handleCancelAiPreview} className="gap-1.5" data-testid="bev-cancel-ai-btn">
              <X className="w-4 h-4" /> Cancelar generación
            </Button>
          ) : (
            <Button variant="outline" onClick={handleGenerateAI} disabled={creating || !form.name.trim() || !form.menuId} className="gap-1.5" data-testid="bev-create-with-ai-btn">
              <Sparkles className="w-4 h-4" />
              {pendingDishId ? "Regenerar con IA" : "Generar imagen con IA"}
            </Button>
          )}
          <Button onClick={handleCreate} disabled={creating || aiPreviewLoading || !form.name.trim() || !form.menuId} className="gap-1.5" data-testid="bev-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Crear bebida
          </Button>
        </DialogFooter>

        <QuickAddIngredientDialog
          open={quickAddOpen}
          onOpenChange={setQuickAddOpen}
          restaurantId={restaurantId}
          onCreated={async (ing) => {
            await reloadIngredients();
            if (ing?.ingredient_id) {
              setForm((f) => ({ ...f, ingredientId: ing.ingredient_id }));
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default NewBeverageDialog;
